import {
  ID,
  ListApiResponse,
  SingleApiResponse
} from '@ianesthesie-kit/services';
import { groupBy, valueOfEnum } from '@ianesthesie-kit/core';
import {
  GroupedLabTests,
  LabTest,
  LabTestResponse,
  LabTestType
} from './labTest.model';
import { ListMedicalService, MedicalResponse } from '../baseMedical.service';
import { GroupFunction } from '@/util';
import axios, { AxiosResponse } from 'axios';
import pickBy from 'lodash/fp/pickBy';
import omitBy from 'lodash/fp/omitBy';
import { stringify } from 'query-string';

export class LabTestService extends ListMedicalService<LabTest> {
  private static INSTANCE_BY_ID: LabTestService;

  private constructor(patientId: string) {
    super('/lab-test', patientId);
  }

  public static getInstance(patientId: ID): LabTestService {
    if (this.shouldCreateNewInstance(this.INSTANCE_BY_ID, patientId)) {
      this.INSTANCE_BY_ID = new LabTestService(patientId);
    }
    return this.INSTANCE_BY_ID;
  }

  public upsert(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    item: Partial<LabTest>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    params: Record<string, any>
  ): Promise<MedicalResponse<LabTest, true>> {
    throw new Error('Unsupported operation');
  }

  public createEmpty(type: LabTestType): Promise<{ data: GroupedLabTests }> {
    return axios
      .post(`${this.url}/${type}`)
      .then((response: AxiosResponse<ListApiResponse<LabTestResponse>>) => ({
        data: response.data.data.map((d) => this.transformResponseEntity(d))
      }))
      .then((resp) => ({
        data: this.getGroupingFunction().apply(resp.data)
      }))
      .catch((err) => {
        throw this.unwrapErrorDetails(err) || err;
      });
  }

  public patch(partial: Partial<LabTest>): Promise<{ data: LabTest }> {
    const isToDelete = (v: unknown) => typeof v !== 'number' && !v;
    const toDelete = Object.keys(pickBy(isToDelete, partial));
    const toPatch = omitBy(isToDelete, partial);
    const deletePromise = this.deleteByCode(partial.id, toDelete);
    return deletePromise.then(() =>
      axios
        .patch(this.url, this.transformToRequestBody(toPatch))
        .then(
          (response: AxiosResponse<SingleApiResponse<LabTestResponse>>) => ({
            data: this.transformResponseEntity(response.data.data)
          })
        )
        .catch((err) => {
          throw this.unwrapErrorDetails(err) || err;
        })
    );
  }

  public deleteGrouped(
    item: ID | LabTest
  ): Promise<MedicalResponse<GroupedLabTests, false>> {
    return this.delete(item).then((resp) => ({
      data: this.getGroupingFunction().apply(resp.data)
    }));
  }

  delete(item: ID | LabTest): Promise<MedicalResponse<LabTest, true>> {
    // temp fix for https://app.clickup.com/t/86bznxdr3
    // give some time for patch requests to finish
    return new Promise((r) => setTimeout(r, 600)).then(() =>
      super.delete(item)
    );
  }

  protected getGroupingFunction<K extends string, L extends boolean>(): {
    apply: GroupFunction<LabTest, K, L>;
  } {
    return {
      apply: (data) => groupBy(data, 'type')
    };
  }

  protected transformResponseEntity(data: LabTestResponse): LabTest {
    const { results, type, ...restData } = data as Extract<
      LabTestResponse,
      { results?: unknown }
    >;
    return {
      type: valueOfEnum(type, LabTestType, {
        throwIfNotFound: true,
        caseSensitive: false
      }) as any,
      ...restData,
      ...results
    };
  }

  protected transformToRequestBody(
    data: Partial<LabTest>
  ): Record<string, unknown> {
    const { id, type, date, comment, ...results } = data;
    const body = { id, type, date, comment, results };
    return pickBy((v) => v !== undefined, body);
  }

  private deleteByCode(itemId: ID | undefined, codes: string[]): Promise<void> {
    if (!itemId) {
      throw new Error('Lab test id is not defined');
    }

    if (!codes?.length) {
      return Promise.resolve();
    }
    const params = stringify({ code: codes });
    return axios.delete(`${this.url}/${itemId}?${params}`);
  }
}
